import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Check } from "lucide-react";
import "./marketing.css";

/**
 * Page shell for the single-task public pages people reach from an email or a
 * shared link: /invite, /review/:token, /reset-password, /claim and the 404
 * page. It is the marketing `.mk` scope with a logo bar and one centred column,
 * so these pages match the site a visitor has just come from. There is no
 * navigation or footer: each page has one job.
 */
export const StandalonePage = ({ children, wide }: { children: ReactNode; wide?: boolean }) => (
  <div className="mk mk-solo">
    <header className="mk-solo__bar">
      <Link to="/" className="mk-header__brand" aria-label="Compaire home">
        <img className="mk-header__logo" src="/compaire-logo.svg" alt="" width={240} height={22} />
      </Link>
    </header>
    <main className="mk-solo__main">
      <div className={wide ? "mk-solo__col mk-solo__col--wide" : "mk-solo__col"}>{children}</div>
    </main>
  </div>
);

/**
 * A white card stating one outcome: an optional mark, a title, body copy and
 * actions. `success` is the teal check badge, `error` a red alert icon and
 * `notice` a muted one.
 */
export const StandalonePanel = ({
  tone,
  title,
  children,
  actions,
}: {
  tone?: "success" | "error" | "notice";
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
}) => (
  <div className="mk-card mk-solo__panel">
    {tone === "success" && (
      <span className="mk-badge-circle" aria-hidden="true">
        <Check size={20} strokeWidth={2.5} />
      </span>
    )}
    {(tone === "error" || tone === "notice") && (
      <AlertCircle
        className={tone === "error" ? "mk-solo__mark mk-solo__mark--error" : "mk-solo__mark"}
        size={28}
        aria-hidden="true"
      />
    )}
    {title && <h1 className="mk-h2 mk-h2--sm">{title}</h1>}
    {children}
    {actions && <div className="mk-solo__actions">{actions}</div>}
  </div>
);

/** The in-progress state: a spinner beside one line of text. */
export const StandaloneBusy = ({ children }: { children: ReactNode }) => (
  <div className="mk-card mk-solo__panel" role="status">
    <div className="mk-solo__busy">
      <span className="mk-spinner mk-spinner--ink" aria-hidden="true" />
      <p className="mk-body">{children}</p>
    </div>
  </div>
);
